import {
  generateDEK,
  encrypt,
  decrypt,
} from './symmetric';
import {
  sign,
  verify,
  ecdhAgreement,
  deriveSharedKey,
} from './asymmetric';
import { zeroMemory } from './utils';

/**
 * An encrypted envelope containing a ciphertext and its wrapped DEK.
 *
 * Security rationale (envelope encryption pattern):
 * 1. A fresh random DEK encrypts the plaintext (data-at-rest).
 * 2. The DEK is encrypted with a key derived from an ECDH shared secret
 *    between sender and recipient (data-in-transit key wrapping).
 * 3. The entire envelope is signed with the sender's Ed25519 key to
 *    provide authenticity and prevent repudiation.
 *
 * This three-layer approach means:
 * - Compromise of the ECDH key does not expose old plaintext (forward
 *   secrecy is partially provided by the per-message DEK).
 * - The recipient can verify who sent the message before decrypting.
 * - Each secret gets its own DEK, enabling fine-grained key rotation.
 */
export interface EncryptedEnvelope {
  /** XChaCha20-Poly1305 ciphertext of the plaintext (includes 16-byte tag) */
  readonly dataCiphertext: Uint8Array;
  /** 24-byte nonce used for data encryption */
  readonly dataNonce: Uint8Array;
  /** XChaCha20-Poly1305 ciphertext of the wrapped DEK */
  readonly wrappedKey: Uint8Array;
  /** 24-byte nonce used for DEK wrapping */
  readonly wrappedKeyNonce: Uint8Array;
  /** Ed25519 signature over (dataNonce || dataCiphertext || wrappedKeyNonce || wrappedKey) */
  readonly signature: Uint8Array;
}

/**
 * Encrypt a plaintext into a signed, sealed envelope for a specific recipient.
 *
 * Flow:
 * 1. Generate a fresh random 32-byte DEK.
 * 2. Encrypt the plaintext with the DEK using XChaCha20-Poly1305 (AAD = empty).
 * 3. Perform X25519 ECDH between sender's private key and recipient's public key.
 * 4. Derive a wrapping key from the shared secret via HKDF-SHA256.
 * 5. Encrypt the DEK with the wrapping key using XChaCha20-Poly1305.
 * 6. Sign (dataNonce || dataCiphertext || wrappedKeyNonce || wrappedKey)
 *    with the sender's Ed25519 private key.
 * 7. Zero the DEK and wrapping key from memory.
 *
 * @param plaintext         - The data to encrypt
 * @param recipientEncPub   - Recipient's 32-byte X25519 public key
 * @param senderEncPriv     - Sender's 32-byte X25519 private key
 * @param senderSignPriv    - Sender's 64-byte Ed25519 private key
 * @returns A sealed {@link EncryptedEnvelope}
 */
export async function encryptEnvelope(
  plaintext: Uint8Array,
  recipientEncPub: Uint8Array,
  senderEncPriv: Uint8Array,
  senderSignPriv: Uint8Array,
): Promise<EncryptedEnvelope> {
  // 1. Generate DEK
  const dek = generateDEK();

  // 2. Encrypt plaintext with DEK
  const { nonce: dataNonce, ciphertext: dataCiphertext } = encrypt(
    plaintext,
    dek,
  );

  // 3-4. ECDH + HKDF to get wrapping key
  const sharedSecret = ecdhAgreement(senderEncPriv, recipientEncPub);
  const wrappingKey = await deriveSharedKey(sharedSecret, 'envelope-dek-wrap');

  // 5. Encrypt (wrap) the DEK
  const { nonce: wrappedKeyNonce, ciphertext: wrappedKey } = encrypt(
    dek,
    wrappingKey,
  );

  // 6. Sign the envelope
  const messageToSign = new Uint8Array(
    dataNonce.length +
    dataCiphertext.length +
    wrappedKeyNonce.length +
    wrappedKey.length,
  );
  let offset = 0;
  messageToSign.set(dataNonce, offset);
  offset += dataNonce.length;
  messageToSign.set(dataCiphertext, offset);
  offset += dataCiphertext.length;
  messageToSign.set(wrappedKeyNonce, offset);
  offset += wrappedKeyNonce.length;
  messageToSign.set(wrappedKey, offset);

  const signature = sign(messageToSign, senderSignPriv);

  // 7. Clean up sensitive intermediates
  zeroMemory(dek);
  zeroMemory(wrappingKey);
  zeroMemory(sharedSecret);

  return {
    dataCiphertext,
    dataNonce,
    wrappedKey,
    wrappedKeyNonce,
    signature,
  };
}

/**
 * Decrypt a sealed envelope, verifying the sender's signature first.
 *
 * Flow:
 * 1. Verify the Ed25519 signature using the sender's public key.
 *    Abort immediately if verification fails.
 * 2. Perform X25519 ECDH between recipient's private key and sender's
 *    public key to recover the shared secret.
 * 3. Derive the wrapping key from the shared secret via HKDF-SHA256.
 * 4. Decrypt the wrapped DEK using the wrapping key.
 * 5. Decrypt the ciphertext using the DEK.
 * 6. Zero the DEK and wrapping key from memory.
 *
 * @param envelope          - The {@link EncryptedEnvelope} to decrypt
 * @param senderSignPub     - Sender's 32-byte Ed25519 public key
 * @param recipientEncPriv  - Recipient's 32-byte X25519 private key
 * @param senderEncPub      - Sender's 32-byte X25519 public key
 * @returns The original plaintext as a Uint8Array
 * @throws If the signature is invalid, the DEK unwrap fails, or the
 *         ciphertext has been tampered with
 */
export async function decryptEnvelope(
  envelope: EncryptedEnvelope,
  senderSignPub: Uint8Array,
  recipientEncPriv: Uint8Array,
  senderEncPub: Uint8Array,
): Promise<Uint8Array> {
  // 1. Reconstruct the signed message
  const messageSigned = new Uint8Array(
    envelope.dataNonce.length +
    envelope.dataCiphertext.length +
    envelope.wrappedKeyNonce.length +
    envelope.wrappedKey.length,
  );
  let offset = 0;
  messageSigned.set(envelope.dataNonce, offset);
  offset += envelope.dataNonce.length;
  messageSigned.set(envelope.dataCiphertext, offset);
  offset += envelope.dataCiphertext.length;
  messageSigned.set(envelope.wrappedKeyNonce, offset);
  offset += envelope.wrappedKeyNonce.length;
  messageSigned.set(envelope.wrappedKey, offset);

  // 1. Verify signature BEFORE any decryption
  const sigValid = verify(messageSigned, envelope.signature, senderSignPub);
  if (!sigValid) {
    throw new Error('Envelope signature verification failed');
  }

  // 2-3. ECDH + HKDF to recover wrapping key
  const sharedSecret = ecdhAgreement(recipientEncPriv, senderEncPub);
  const wrappingKey = await deriveSharedKey(sharedSecret, 'envelope-dek-wrap');

  // 4. Unwrap the DEK
  const dek = decrypt(
    envelope.wrappedKey,
    envelope.wrappedKeyNonce,
    wrappingKey,
  );

  // 5. Decrypt the plaintext
  const plaintext = decrypt(
    envelope.dataCiphertext,
    envelope.dataNonce,
    dek,
  );

  // 6. Clean up
  zeroMemory(dek);
  zeroMemory(wrappingKey);
  zeroMemory(sharedSecret);

  return plaintext;
}
